/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

/**
 * The policy-type tabs. `isPolicyTypeValue` separates the tabs that ARE a backend
 * policy_type from the one that only chooses which form is shown — the partner tab's
 * policy_type comes from its own Policy layer control (provider / recipient).
 */
export const PARTNER_PROGRAM_POLICY_TAB = "partner_benefit_program";

export const POLICY_TYPE_OPTIONS = [
  { label: "Group", value: "group", isPolicyTypeValue: true },
  { label: "Restaurant", value: "restaurant", isPolicyTypeValue: true },
  { label: "Partner Benefit Program", value: PARTNER_PROGRAM_POLICY_TAB, isPolicyTypeValue: false },
];

/** The tabs whose value is sent as `policy_type` verbatim. */
export const STANDARD_POLICY_TYPES = POLICY_TYPE_OPTIONS
  .filter((option) => option.isPolicyTypeValue)
  .map((option) => option.value);

/**
 * @description Whether a tab drives the standard (group / restaurant) form.
 * @param policyType The selected tab value.
 * @returns True for the group and restaurant tabs.
 */
export const isStandardPolicyTab = (policyType) => STANDARD_POLICY_TYPES.includes(policyType);

// `verification_method` names the population a policy governs. `manual` is the
// only value the backend whitelists today, so the control is pre-set and locked.
export const APPLIES_TO_OPTIONS = [{ label: "Manual", value: "manual" }];

export const NOTIFICATION_RECIPIENT_OPTIONS = [
  { label: "Email to admin", value: "admin" },
  { label: "Email to employee/guest", value: "user" },
  { label: "Email to both", value: "all" },
];

export const VERIFICATION_FREQUENCY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Annually", value: "annually" },
];

export const INACTIVITY_CHECK_OPTIONS = [
  { label: "Daily login", value: "last_login" },
  { label: "Active discount card", value: "active_discount_card" },
  { label: "Has Check-In", value: "check_in" },
];

// Mirrors CardPolicyRule::VERIFICATION_FREQUENCY_DAYS. The grace period has to
// fit inside a single verification cycle, so the check runs client-side too
// instead of waiting for the 422. See describeGracePeriodError.
const VERIFICATION_FREQUENCY_CYCLE_DAYS = {
  daily: 1,
  weekly: 7,
  monthly: 30,
  quarterly: 90,
  annually: 365,
};

export const DEFAULT_INACTIVITY_CHECKS = ["last_login"];
export const DEFAULT_INACTIVITY_PERIOD_DAYS = 15;

/**
 * Single source of truth for the policy form. The same shape backs the create
 * and the update flow, so every control reads and writes one state object that a
 * fetched policy can be mapped onto (see mapPolicyToConfiguration).
 */
export const INITIAL_POLICY_CONFIGURATION = {
  draft: false,
  name: "",
  policy_type: "group",
  restaurant_group_id: "",
  restaurant_ids: [],
  description: "",
  // Edit-mode only. `status` is the policy's persisted status, kept so the draft
  // toggle can tell an actual status change from a no-op; `scope_name` is the
  // already-resolved name of the locked scope; `persisted_rules` is the rule set
  // the policy was loaded with, which an update diffs against.
  status: "",
  scope_name: "",
  persisted_rules: [],
  // Effective rules. An empty string / empty array / null means "not set", which
  // is what keeps the rule out of the payload so the parent policy is inherited.
  verification_method: "manual",
  notification_recipients: "",
  verification_frequency: "",
  max_active_users: "",
  exceptions_permitted: null,
  exception_network_guest_ids: [],
  grace_period_days: "",
  inactivity_rule_enabled: false,
  inactivity_checks: [],
  inactivity_period_days: "",
};

export const sanitizeIntegerInput = (value) => `${value ?? ""}`.replace(/[^0-9]/g, "");

export const isIntegerFieldSet = (value) => `${value ?? ""}`.trim() !== "";

/**
 * The endpoint requires all five inactivity-review rules together or none at all,
 * so the asterisks appear across the whole group the moment any one is set.
 */
export const INACTIVITY_REVIEW_FIELDS = [
  "notification_recipients",
  "verification_frequency",
  "grace_period_days",
  "inactivity_checks",
  "inactivity_period_days",
];

/**
 * @description Whether the author has set any inactivity-review rule, which makes
 * every rule in that group mandatory.
 * @param policyConfigurationData The current form state.
 * @returns True when at least one of the five review rules carries a value.
 */
export const hasAnyInactivityReviewField = (policyConfigurationData) =>
  INACTIVITY_REVIEW_FIELDS.some((field) => {
    const value = policyConfigurationData?.[field];

    return Array.isArray(value) ? value.length > 0 : isIntegerFieldSet(value);
  });

/**
 * @description Builds the `rules` array for the policy engine create endpoint,
 * omitting every rule the author left unset so it inherits from the parent.
 * @param policyConfigurationData The current form state.
 * @returns The rules array accepted by the policy engine endpoint.
 */
export const buildPolicyRules = (policyConfigurationData) => {
  const rules = [
    {
      rule_key: "verification_method",
      rule_value: policyConfigurationData?.verification_method || "manual",
    },
  ];

  if (policyConfigurationData?.notification_recipients) {
    rules.push({
      rule_key: "notification_recipients",
      rule_value: policyConfigurationData.notification_recipients,
    });
  }

  if (policyConfigurationData?.verification_frequency) {
    rules.push({
      rule_key: "verification_frequency",
      rule_value: policyConfigurationData.verification_frequency,
    });
  }

  if (isIntegerFieldSet(policyConfigurationData?.max_active_users)) {
    rules.push({
      rule_key: "max_active_users",
      rule_value: Number(policyConfigurationData.max_active_users),
    });
  }

  if (policyConfigurationData?.exceptions_permitted !== null && policyConfigurationData?.exceptions_permitted !== undefined) {
    rules.push({
      rule_key: "exceptions_permitted",
      rule_value: Boolean(policyConfigurationData.exceptions_permitted),
    });
  }

  // The switch and the list are one setting split over two rules; the backend
  // rejects either without the other, so the list ships only while the switch is on.
  if (policyConfigurationData?.exceptions_permitted === true) {
    rules.push({
      rule_key: "exception_network_guest_ids",
      rule_value: policyConfigurationData?.exception_network_guest_ids || [],
    });
  }

  if (isIntegerFieldSet(policyConfigurationData?.grace_period_days)) {
    rules.push({
      rule_key: "grace_period_days",
      rule_value: Number(policyConfigurationData.grace_period_days),
    });
  }

  // `inactivity_checks` and `inactivity_period_days` are one setting split over
  // two rules; the backend rejects a half-configured pair, so they ship together.
  if (policyConfigurationData?.inactivity_rule_enabled) {
    rules.push({
      rule_key: "inactivity_checks",
      rule_value: policyConfigurationData?.inactivity_checks || [],
    });
    rules.push({
      rule_key: "inactivity_period_days",
      rule_value: Number(policyConfigurationData?.inactivity_period_days),
    });
  }

  return rules;
};

/**
 * @description Builds the full create-policy request body from the form state.
 * @param policyConfigurationData The current form state.
 * @returns The payload for POST /v1/admin/policy_engines.
 */
export const buildPolicyPayload = (policyConfigurationData) => {
  const isRestaurantPolicy = policyConfigurationData?.policy_type === "restaurant";

  return {
    // Only the scope key that matches the policy type is sent. A restaurant policy
    // derives its group from the target restaurants server-side, and sending a
    // group alongside them makes the backend cross-check the two — which a stale
    // value from a since-hidden filter would fail.
    restaurant_group_id: isRestaurantPolicy ? undefined : policyConfigurationData?.restaurant_group_id,
    policy_type: policyConfigurationData?.policy_type,
    name: `${policyConfigurationData?.name || ""}`.trim(),
    description: `${policyConfigurationData?.description || ""}`.trim() || undefined,
    draft: Boolean(policyConfigurationData?.draft),
    restaurant_ids: isRestaurantPolicy ? policyConfigurationData?.restaurant_ids : undefined,
    rules: buildPolicyRules(policyConfigurationData),
  };
};

// Maps a persisted rule onto the form fields that represent it. Values arrive as
// their JSON types; the number fields are text inputs, so they are stringified.
const RULE_CONFIGURATION_MAPPERS = {
  verification_method: (ruleValue) => ({ verification_method: ruleValue || "manual" }),
  notification_recipients: (ruleValue) => ({ notification_recipients: ruleValue || "" }),
  verification_frequency: (ruleValue) => ({ verification_frequency: ruleValue || "" }),
  max_active_users: (ruleValue) => ({ max_active_users: `${ruleValue ?? ""}` }),
  exceptions_permitted: (ruleValue) => ({ exceptions_permitted: Boolean(ruleValue) }),
  exception_network_guest_ids: (ruleValue) => ({
    exception_network_guest_ids: Array.isArray(ruleValue) ? ruleValue : [],
  }),
  grace_period_days: (ruleValue) => ({ grace_period_days: `${ruleValue ?? ""}` }),
  inactivity_checks: (ruleValue) => ({
    inactivity_rule_enabled: true,
    inactivity_checks: Array.isArray(ruleValue) ? ruleValue : [],
  }),
  inactivity_period_days: (ruleValue) => ({
    inactivity_rule_enabled: true,
    inactivity_period_days: `${ruleValue ?? ""}`,
  }),
};

// hasOwnProperty, not a bare lookup: a rule_key colliding with an inherited
// Object member ("constructor") would otherwise resolve to a truthy non-mapper.
const isFormManagedRule = (rule) => (
  Object.prototype.hasOwnProperty.call(RULE_CONFIGURATION_MAPPERS, `${rule?.rule_key}`)
);

/** Drops the ids the show endpoint returns, leaving only what an update sends. */
const toRulePairs = (rules) => (
  (Array.isArray(rules) ? rules : []).map((rule) => ({ rule_key: rule?.rule_key, rule_value: rule?.rule_value }))
);

/**
 * @description Order-insensitive signature of a rule set, used to tell an edited
 * rule set from an untouched one. Rule keys are unique per policy, so sorting by
 * key is enough to make two equivalent sets compare equal.
 * @param rules A rule set.
 * @returns A string that is equal for equivalent rule sets.
 */
const canonicalizeRules = (rules) => JSON.stringify(
  toRulePairs(rules).sort((left, right) => `${left.rule_key}`.localeCompare(`${right.rule_key}`))
);

/**
 * @description The rule set a submit would write: everything this form owns, plus
 * the persisted rules it has no control for — `rules` replaces the whole set, so
 * those have to ride along or the update would delete them.
 * @param policyConfigurationData The current form state.
 * @returns The rule set that would be sent.
 */
const buildNextRuleSet = (policyConfigurationData) => [
  ...buildPolicyRules(policyConfigurationData),
  ...toRulePairs(policyConfigurationData?.persisted_rules).filter((rule) => !isFormManagedRule(rule)),
];

/**
 * @description Whether this submit would replace the persisted rule set. An update
 * that leaves every rule alone sends no `rules`, and the endpoint skips every
 * cross-rule check in that case (`rules_provided?`) — so the form has to skip them
 * too, or a policy already holding an incomplete rule set could never be renamed.
 * Always true for a create, which has no persisted rules to match.
 * @param policyConfigurationData The current form state.
 * @returns True when the rule set is being written.
 */
export const replacesPolicyRuleSet = (policyConfigurationData) => (
  canonicalizeRules(buildNextRuleSet(policyConfigurationData))
    !== canonicalizeRules(policyConfigurationData?.persisted_rules)
);

/**
 * @description Maps a policy fetched from the show endpoint onto the form state,
 * so the edit modal opens on exactly what is persisted. The rule set is kept
 * verbatim as `persisted_rules` for the update to diff against.
 * @param policy The policy returned by GET /v1/admin/policy_engines/:id.
 * @param scopeName Display name of the policy's scope, already resolved by the listing.
 * @returns Form state for PolicyEngineConfigurationModal.
 */
export const mapPolicyToConfiguration = (policy, scopeName = "") => {
  const rules = Array.isArray(policy?.rules) ? policy.rules : [];

  const ruleFields = rules.reduce((accumulator, rule) => {
    const mapRule = isFormManagedRule(rule) ? RULE_CONFIGURATION_MAPPERS[rule.rule_key] : null;
    return mapRule ? { ...accumulator, ...mapRule(rule?.rule_value) } : accumulator;
  }, {});

  return {
    ...INITIAL_POLICY_CONFIGURATION,
    draft: policy?.status === "draft",
    status: policy?.status || "",
    name: policy?.name || "",
    policy_type: policy?.policy_type || "group",
    restaurant_group_id: policy?.restaurant_group_id || "",
    restaurant_ids: policy?.restaurant_id ? [policy.restaurant_id] : [],
    description: policy?.description || "",
    scope_name: scopeName || "",
    persisted_rules: toRulePairs(rules),
    ...ruleFields,
  };
};

/**
 * @description Builds the update-policy request body from the form state. Scope
 * and policy type are absent by design: the endpoint does not accept them, so an
 * existing policy keeps the scope it was created with.
 * @param policyConfigurationData The current form state.
 * @returns The payload for PUT /v1/admin/policy_engines/:id.
 */
export const buildPolicyUpdatePayload = (policyConfigurationData) => {
  const isDraft = Boolean(policyConfigurationData?.draft);
  const wasDraft = policyConfigurationData?.status === "draft";

  return {
    name: `${policyConfigurationData?.name || ""}`.trim(),
    description: `${policyConfigurationData?.description || ""}`.trim(),
    // Only sent when the toggle actually moved. Deriving it unconditionally would
    // activate an archived policy the moment anything else on it was edited.
    status: isDraft === wasDraft ? undefined : (isDraft ? "draft" : "active"),
    // Omitted when the author did not touch a rule: supplying `rules` makes the
    // endpoint re-validate the effective cap against live headcount, which would
    // otherwise let count drift block an edit that only renamed the policy.
    rules: replacesPolicyRuleSet(policyConfigurationData)
      ? buildNextRuleSet(policyConfigurationData)
      : undefined,
  };
};

/**
 * The label each required field is shown under, so a message names the control
 * the author has to go back to rather than the rule_key behind it. "Verification
 * method" is the notification_recipients row; the verification_method rule is the
 * locked "Applies to" control, which can never be blank.
 */
export const POLICY_FIELD_LABELS = {
  name: "Policy name",
  restaurant_group_id: "Restaurant group",
  restaurant_ids: "Restaurant",
  notification_recipients: "Verification method",
  verification_frequency: "Verification frequency",
  grace_period_days: "Grace period (days)",
  inactivity_checks: "Inactivity rules",
  inactivity_period_days: "Inactivity period",
  exception_network_guest_ids: "Excepted users",
};

/**
 * @description Every required field the author has left blank, named by its form
 * label. Mirrors the asterisks the modal renders — the scope key for the chosen
 * policy type, the five inactivity-review rules once any one of them carries a
 * value, and the excepted users once exceptions are switched on — so the endpoint
 * is never asked to reject a rule set the form could already tell was incomplete.
 * @param policyConfigurationData The current form state.
 * @returns The blank required field names, in the order the form shows them.
 */
export const collectMissingRequiredPolicyFields = (policyConfigurationData) => {
  const isFieldSet = (field) => {
    const value = policyConfigurationData?.[field];

    return Array.isArray(value) ? value.length > 0 : isIntegerFieldSet(value);
  };

  // The rule requirements are cross-rule checks, which the endpoint only applies to
  // a rule set it is actually being sent. Applying them to an untouched one would
  // strand a policy that already holds an incomplete set — it could not even be
  // renamed. Name and scope are not rule checks, so they always apply.
  const ruleFieldsRequired = replacesPolicyRuleSet(policyConfigurationData);

  const requiredFields = [
    "name",
    // Each policy type requires only its own scope key — the other filter is hidden.
    policyConfigurationData?.policy_type === "restaurant" ? "restaurant_ids" : "restaurant_group_id",
    // The review rules are all-or-nothing, so one of them being set makes the rest
    // mandatory — the same condition the asterisks render on.
    ...(ruleFieldsRequired && hasAnyInactivityReviewField(policyConfigurationData)
      ? INACTIVITY_REVIEW_FIELDS
      : []),
    ...(ruleFieldsRequired && policyConfigurationData?.exceptions_permitted === true
      ? ["exception_network_guest_ids"]
      : []),
  ];

  return requiredFields.filter((field) => !isFieldSet(field));
};

/**
 * The only fields that still render a message beside them. Every rule control
 * signals its problem with an outline instead, so a rule message that the missing
 * -field list does not already name has to reach the snackbar or it would be lost.
 */
export const INLINE_MESSAGE_FIELDS = ["name", "restaurant_group_id", "restaurant_ids"];

/**
 * @description The blank required fields named by their form label, for a message
 * that has to read as a sentence rather than drive a control.
 * @param policyConfigurationData The current form state.
 * @returns The labels of the blank required fields, in the order the form shows them.
 */
export const describeMissingPolicyFields = (policyConfigurationData) => (
  collectMissingRequiredPolicyFields(policyConfigurationData).map((field) => POLICY_FIELD_LABELS[field])
);

/**
 * @description The message for a grace period outside the window its verification
 * cycle allows. Mirrors RuleValidatable#validate_grace_period_within_verification_cycle:
 * at least a day, since 0 mints a link that is already expired when it arrives,
 * and at most one whole cycle, since only the newest cycle is ever surfaced.
 * @param gracePeriodDays The entered grace period, or null when left blank.
 * @param frequency The selected verification frequency.
 * @returns The error message, or null when the grace period is in range.
 */
const describeGracePeriodError = (gracePeriodDays, frequency) => {
  if (gracePeriodDays === null) {
    return null;
  }

  const cycleDays = VERIFICATION_FREQUENCY_CYCLE_DAYS[frequency];

  // No frequency picked yet, so there is no cycle to measure against and only the
  // floor applies — the missing-required-fields list names the frequency itself.
  if (!cycleDays) {
    return gracePeriodDays < 1 ? "Grace period must be at least 1 day" : null;
  }

  if (gracePeriodDays >= 1 && gracePeriodDays <= cycleDays) {
    return null;
  }

  // A daily cycle leaves exactly one legal value, so the message names it.
  return cycleDays === 1
    ? "Grace period cannot be less than or greater than 1"
    : `Grace period must be between 1 and the ${frequency} verification cycle (${cycleDays} days)`;
};

/**
 * @description Validates the parts of the form the endpoint would otherwise
 * reject with a 422, so the author sees the problem next to the field.
 * @param policyConfigurationData The current form state.
 * @returns A map of field name to error message; empty when the form is valid.
 */
export const validatePolicyConfiguration = (policyConfigurationData) => {
  const errors = {};

  if (!`${policyConfigurationData?.name || ""}`.trim()) {
    errors.name = "Policy name is required";
  }

  // Each policy type requires only its own scope key — the other filter is hidden.
  if (policyConfigurationData?.policy_type === "restaurant") {
    if (!policyConfigurationData?.restaurant_ids?.length) {
      errors.restaurant_ids = "Select at least one restaurant";
    }
  } else if (!policyConfigurationData?.restaurant_group_id) {
    errors.restaurant_group_id = "Restaurant group is required";
  }

  // Everything below is a rule check, which the endpoint only applies to a rule set
  // it is being sent — see replacesPolicyRuleSet.
  if (!replacesPolicyRuleSet(policyConfigurationData)) {
    return errors;
  }

  if (policyConfigurationData?.exceptions_permitted === true
    && !policyConfigurationData?.exception_network_guest_ids?.length) {
    errors.exception_network_guest_ids = "Select at least one user to except";
  }

  if (policyConfigurationData?.inactivity_rule_enabled) {
    if (!policyConfigurationData?.inactivity_checks?.length) {
      errors.inactivity_checks = "Select at least one activity signal";
    }

    if (!(Number(policyConfigurationData?.inactivity_period_days) > 0)) {
      errors.inactivity_period_days = "Inactivity period must be greater than 0";
    }
  }

  const gracePeriodError = describeGracePeriodError(
    isIntegerFieldSet(policyConfigurationData?.grace_period_days)
      ? Number(policyConfigurationData.grace_period_days)
      : null,
    policyConfigurationData?.verification_frequency
  );

  if (gracePeriodError) {
    errors.grace_period_days = gracePeriodError;
  }

  return errors;
};

export const ACTIVE_POLICY_CHECKING_TEXT = "Checking for active policy...";
export const ACTIVE_POLICY_CLEAR_TEXT = "No active policy found you can proceed to create";
